import {
  Box,
  Flex,
  Text,
  useColorModeValue,
  Spinner,
  Heading,
  VStack,
  Center,
} from "@chakra-ui/react";
import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  LabelList,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import API_CONFIG from "../../Configuration/apiConfig";
import apiClient from "../../Configuration/apiClient";
import { getCurrentDate, getFirstDayOfMonth } from "../../Utils/dateUtils";
import { useDateFilter } from "../../Hooks/useDateFilter";
import { SCREEN_KEYS } from "../../constants/screenKeys";
import DateRangeFilter from "../../components/DateRangeFilter/DateRangeFilter";


export default function Dashboard() {
  const iconBlue = useColorModeValue("red.500", "red.500");
  const [error, setError] = useState(null);
  const [storeAuditedCount, setStoreAuditedCount] = useState(0);
  const [storeTypeChartData, setStoreTypeChartData] = useState([]);
  const [storeTypeChartLoading, setStoreTypeChartLoading] = useState(false);
  const [regionChartData, setRegionChartData] = useState([]);
  const [regionChartLoading, setRegionChartLoading] = useState(false)
  const [redScore, setRedScore] = useState(0);
  const [availabilityScore, setAvailabilityScore] = useState(0);
  const [activationScore, setActivationScore] = useState(0);
  const [coolerScore, setCoolerScore] = useState(0);
  const [bonusScore, setBonusScore] = useState(0);
  const [totalOutlets, setTotalOutlets] = useState(0);
  const [loadingScores, setLoadingScores] = useState(false);


  const ALL_REGIONS = [
    "E&D",
    "TRAVEL",
    "GROCERY",
    "MINIMART",
    " LIQUORSHOP",
    "SUPERMARKET",
    "CINEMA",
    "WHOLESALER"
  ]
  const ALL_CHANNELS = [
    "EAST",
    "WEST",
    "CENTRAL",
    "BNL - GT",
    "KEY ACCOUNT",
  ];


  const { fromDate, toDate, setFromDate, setToDate } = useDateFilter(
    SCREEN_KEYS.DASHBOARD,
    { fromDate: getFirstDayOfMonth(), toDate: getCurrentDate() }
  );

  const handleFromDateChange = (value) => {
    setFromDate(value);
    if (value > toDate) setToDate(value);
  };

  const handleToDateChange = (value) => {
    if (value < fromDate) return;
    setToDate(value);
  };

  const cards = [
    { label: " Average Total RedScore", value: redScore, variant: "score-card--red" },
    {
      label: "Average Availability",
      value: availabilityScore,
      variant: "score-card--green",
    },
    { label: "Average Activation", value: activationScore, variant: "score-card-red" },
    { label: "Average Cooler", value: coolerScore, variant: "score-card--peach" },
    { label: "Average Cooler Bonus", value: bonusScore, variant: "score-card--blue" },


  ];
  const colors = ["red.500", "green.500", "yellow.500", "red.500", "blue.500", "pink.500"];

  useEffect(() => {
    const fetchScores = async () => {
      if (!fromDate || !toDate) {
        setRedScore(0);
        setAvailabilityScore(0);
        setActivationScore(0);
        setCoolerScore(0);
        setBonusScore(0);
        setTotalOutlets(0);
        return;
      }
      setLoadingScores(true);
      try {
        let filters = {};
        const [fromYear, fromMonthNum, fromDay] = fromDate.split("-").map(Number);
        const [toYear, toMonthNum, toDay] = toDate.split("-").map(Number);

        const requestBody = {
          filters: filters,
          timePeriod: {
            from: {
              date: fromDay,
              month: fromMonthNum,
              year: fromYear
            },
            to: {
              date: toDay,
              month: toMonthNum,
              year: toYear
            }
          }
        };
        const res = await apiClient.post(
          `${API_CONFIG.ENDPOINTS.ASM_REPORT.SCORES}`,
          requestBody

        );
        setRedScore(res.data.totalRedScore || 0);
        setAvailabilityScore(res.data.availabilityScore || 0);
        setActivationScore(res.data.activationScore || 0);
        setCoolerScore(res.data.coolerScore || 0);
        setBonusScore(res.data.coolerBonus || 0);
        setTotalOutlets(res.data.totalOutlets || 0);
      } catch (err) {
        console.error("Failed to load scores:", err);
        setError("Failed to load scores data.");
        setRedScore(0);
        setAvailabilityScore(0);
        setActivationScore(0);
        setCoolerScore(0);
        setBonusScore(0);
        setTotalOutlets(0);
      } finally {
        setLoadingScores(false)
      }
    }
    fetchScores();

  }, [fromDate, toDate])

  useEffect(() => {
    const fetchAuditedStoresData = async () => {
      if (!fromDate || !toDate) return;

      setStoreTypeChartLoading(true);
      setRegionChartLoading(true);
      setError(null);

      try {

        const [fromYear, fromMonthNum, fromDay] = fromDate.split("-").map(Number);
        const [toYear, toMonthNum, toDay] = toDate.split("-").map(Number);

        const requestBody = {
          from: {
            date: fromDay,
            month: fromMonthNum,
            year: fromYear
          },
          to: {
            date: toDay,
            month: toMonthNum,
            year: toYear
          }

        }
        const response = await apiClient.post(
          `${API_CONFIG.ENDPOINTS.DASHBOARD}`, requestBody
        );

        if (response.data) {
          setStoreAuditedCount(response.data.totalStores || 0);
          if (response.data.barGraphResponse && typeof response.data.barGraphResponse === 'object') {
            const barResponse = response.data.barGraphResponse || {};
            const channelData = ALL_CHANNELS.map((channel) => ({
              name: channel,
              value: barResponse[channel] || 0,
            }));

            setStoreTypeChartData(channelData);
          }

          if (response.data.pieChartResponse && typeof response.data.pieChartResponse === 'object') {
            const pieResponse = response.data.pieChartResponse || {};

            const regionData = ALL_REGIONS.map((region) => ({
              name: region,
              value: pieResponse[region] || 0,
            }));

            setRegionChartData(regionData);
          }
        } else {

          setStoreAuditedCount(0);
          setStoreTypeChartData([]);
          setRegionChartData([]);
        }

      } catch (err) {
        const errorMessage = err.response
          ? `Error ${err.response.status}: ${err.response.data.message || "Failed to fetch data."}`
          : "Network error. Please check your connection.";
        setError(errorMessage);

        setStoreAuditedCount(0);
        setStoreTypeChartData([]);
        setRegionChartData([]);
      } finally {
        setStoreTypeChartLoading(false);
        setRegionChartLoading(false);
      }
    };
    fetchAuditedStoresData();
  }, [fromDate, toDate]);


  const bgWhite = useColorModeValue("white", "gray.800");
  const textDark = useColorModeValue("gray.800", "white");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  return (
    <Flex
      flexDirection="column"
      pt={{ base: "120px", md: "75px" }}
      bg={useColorModeValue("gray.50", "gray.900")}
      minH="100vh"
      pb={0}
    >
      {/* Header Section */}
      <Flex justify="space-between" align="center" mb={8}>
        <Box>
          <Heading size="lg" color={textDark} mb={2}>
            Data Analytics Dashboard
          </Heading>
          <Text color="gray.500">
            Comprehensive overview of your data metrics and insights
          </Text>
        </Box>

        <DateRangeFilter
          fromDate={fromDate}
          toDate={toDate}
          onFromDateChange={handleFromDateChange}
          onToDateChange={handleToDateChange}
        />
      </Flex>


      <Flex gap={4} flexWrap="wrap">
        {cards.map((card, index) => (
          <Box
            key={card.label}
            bg="white"
            color="gray.800"
            p={4}
            borderRadius="xl"
            textAlign="center"
            borderBottomWidth="6px"
            borderBottomColor="gray.800"
            boxShadow="md"
            _hover={{
              boxShadow: "md",
              transform: "translateY(-2px)",
            }}
            transition="all 0.2s"
            flex="1"
            minW={{ base: "100%", sm: "45%", md: "auto" }}
          >
            <Text fontSize="md" fontWeight="medium" color={textDark}>
              {card.label}
            </Text>
            {loadingScores ? (
              <Center h="40px">
                <Spinner size="sm" color={colors[index % colors.length]} />
              </Center>
            ) : (
              <Text
                fontWeight="bold"
                fontSize="xl"
                color={colors[index % colors.length]}
              >
                {`${card.value}`}
              </Text>
            )}
          </Box>

        ))}
        <Box
          bg="gray.800"
          color="white"
          px="4"
          py="3"
          borderRadius="lg"
          minW="150px"
          textAlign="center"
          fontWeight="bold"
          boxShadow="sm"
          width={{ base: "100%", lg: "auto" }}
        >
          <Text fontSize="sm" opacity={0.9}>
            Total Outlets
          </Text>
          <Text fontSize="2xl" fontWeight="bold">
            {totalOutlets}
          </Text>
        </Box>
      </Flex>

      {/* Store Audited Details Section */}
      <Box mb={2} mt={5}>
        <Heading size="lg" color={textDark} mb={3}>
          Store Audited Details
        </Heading>
        <Box
          bg="white"
          p={5}
          rounded="xl"
          shadow="md"
          w="100%"
          mb={8}
        >

          {/* Bar Chart Section */}
          <Box mt={6} height="100%" pt="20px">
            {storeTypeChartLoading ? (
              <Center h="300px">
                <VStack spacing={4}>
                  <Spinner size="xl" color={iconBlue} thickness="4px" />
                  <Text color="gray.500">Loading chart data...</Text>
                </VStack>
              </Center>
            ) : storeTypeChartData.length === 0 || storeTypeChartData.every(item => item.value === 0) ? (
              <Center h="300px" sx={{
                '@media screen and (min-width: 1441px)': {
                  height: '550px !important'
                }
              }}
              >
                <VStack spacing={2}>
                  <Text textAlign="center" fontSize="lg" fontWeight="semibold" color="gray.600">
                    No data available
                  </Text>

                </VStack>
              </Center>
            ) : (
              <Box
                w="100%"
                h="100%"
                position="relative"
                className="bar-chart-container">
                <Text fontSize="lg" fontWeight="bold" color={textDark} mb={20} textAlign="center" >
                  Region Level Auditing Stores
                </Text>
                <ResponsiveContainer width="100%" height={450}>
                  <BarChart
                    height={450}
                    data={storeTypeChartData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"
                      vertical={false} />
                    <XAxis
                      dataKey="name"
                      axisLine={{ stroke: "#CBD5E0" }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      interval={0}
                      tick={{
                        fontSize: 12,
                        fill: "#4A5568",
                        fontWeight: 500,
                      }}
                      label={{
                        value: "Regions",
                        position: "insideBottom",
                        offset: -10,
                        dx: -10,
                        style: { fontSize: 18, fill: '#4A5568', fontWeight: 600 }
                      }}

                    />
                    <YAxis tickLine={false}
                      tick={{
                        fontSize: 13,
                        fill: "#07090bff",
                        fontWeight: 500,
                      }}
                      axisLine={{ stroke: "#CBD5E0" }}
                      width={60}
                      tickFormatter={(value) => `${value}`}
                      allowDecimals={false}
                      label={{
                        position: 'insideLeft',
                        content: ({ viewBox }) => {
                          const { x, y, height } = viewBox;
                          return (
                            <text
                              x={x + 25}
                              y={y + height / 2 + 20}
                              textAnchor="middle"
                              transform={`rotate(-90, ${x - 10}, ${y + height / 2})`}
                              style={{ fontSize: 18, fill: '#4A5568', fontWeight: 600 }}
                            >
                              No. of  Stores
                            </text>
                          );
                        }

                      }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: bgWhite,
                        border: `1px solid ${borderColor}`,
                        borderRadius: "8px",
                      }}
                    />
                    <Bar dataKey="value" fill="#c84145ff" barSize={80} radius={[8, 8, 0, 0]}>
                      <LabelList dataKey="value" position="top" fill="#F40009" fontSize={14} />
                    </Bar>

                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Channel Level Bar Chart - Converted from Pie Chart */}
      <Box
        bg="white"
        p={5}
        rounded="xl"
        shadow="md"
        w="100%"
        mb={0}
        pb={5}
      >
        <Text fontSize="lg" fontWeight="bold" color={textDark} mb={20} textAlign="center" >
          Channel Level Auditing Stores
        </Text>
        {regionChartLoading ? (
          <Center h="400px">
            <VStack spacing={4}>
              <Spinner size="xl" color={iconBlue} thickness="4px" />
              <Text color="gray.500">Loading chart data...</Text>
            </VStack>
          </Center>
        ) : regionChartData.length === 0 || regionChartData.every(item => item.value === 0) ? (
          <Center h="400px"
            sx={{
              '@media screen and (min-width: 1441px)': {
                height: '550px !important'
              }
            }}>
            <VStack spacing={2}>
              <Text textAlign="center" fontSize="lg" fontWeight="semibold" color="gray.600">
                No data available
              </Text>
            </VStack>
          </Center>
        ) : (
          <Box
            w="100%"
            h="100%"
          >
            <ResponsiveContainer width="100%" height={450}>
              <BarChart
                height={450}
                data={regionChartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"
                  vertical={false} />
                <XAxis
                  dataKey="name"
                  axisLine={{ stroke: "#CBD5E0" }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  interval={0}
                  tick={{
                    fontSize: 12,
                    fill: "#4A5568",
                    fontWeight: 500,
                  }}
                  label={{
                    value: "Channels",
                    position: "insideBottom",
                    offset: -20,
                    dx: -20,
                    style: { fontSize: 18, fill: '#4A5568', fontWeight: 600 }
                  }}

                />

                <YAxis
                  axisLine={{ stroke: "#CBD5E0" }}
                  tickLine={false}
                  tick={{
                    fontSize: 14,
                    fill: "#07090bff",
                    fontWeight: 500,
                  }}
                  allowDecimals={false}
                  width={60}
                  tickFormatter={(value) => `${value}`}
                  label={{
                    position: 'insideLeft',
                    content: ({ viewBox }) => {
                      const { x, y, height } = viewBox;
                      return (
                        <text
                          x={x + 25}
                          y={y + height / 2 + 20}
                          textAnchor="middle"
                          transform={`rotate(-90, ${x - 10}, ${y + height / 2})`}
                          style={{ fontSize: 18, fill: '#4A5568', fontWeight: 600 }}
                        >
                          No. of  Stores
                        </text>
                      );
                    }

                  }}
                />

                <Tooltip
                  formatter={(value, name) => [`${value} stores`, name]}
                  contentStyle={{
                    backgroundColor: bgWhite,
                    border: `1px solid ${borderColor}`,
                    borderRadius: "8px",
                  }}
                />
                <Bar dataKey="value" fill="#c84145ff" radius={[8, 8, 0, 0]}>
                  <LabelList dataKey="value" position="top" fill="#F40009" fontSize={14} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Box>
    </Flex >
  );
}
